// Legal/compliance page content for Heliophant.
// Each entry: { title, lastUpdated, sections: [{ heading, body?: string[], bullets?: string[] }] }
// Two languages: en, el. Routes pair with this map: en→/terms, el→/terms-gr, etc.

const EN = {
  terms: {
    title: 'Terms of Service',
    lastUpdated: 'February 2026',
    sections: [
      {
        heading: '1. Introduction',
        body: [
          'Welcome to Heliophant. By accessing or using our website, platform, subscription services, digital tools, reports, or analytical content, you agree to these Terms of Service.',
        ],
      },
      {
        heading: '2. Business Information',
        body: [
          'Heliophant provides subscription-based access to proprietary data analysis tools, scientific analytical models, and research-oriented market analysis resources.',
          'Contact email: contact@heliophant.com',
          'Support email: support@heliophant.com',
          'Billing email: billing@heliophant.com',
        ],
      },
      {
        heading: '3. Nature of Services',
        body: [
          'Heliophant provides financial data analysis tools, scientific models, quantitative research resources, and analytical content. The services are intended for informational, educational, research, and decision-support purposes only.',
        ],
      },
      {
        heading: '4. No Investment Advice',
        body: [
          'Heliophant does not provide investment advice, financial advice, portfolio management, brokerage services, asset management, or personalized buy/sell recommendations.',
          'Any information, model output, analysis, chart, signal-like indication, or market commentary provided through Heliophant must not be interpreted as a recommendation to buy, sell, hold, or trade any financial instrument, cryptocurrency, security, derivative, or other asset.',
          'Users remain solely responsible for their own financial decisions and should consult a licensed financial advisor before making investment decisions.',
        ],
      },
      {
        heading: '5. Subscriptions',
        body: [
          'Heliophant may offer monthly or annual subscription plans. Subscription access is granted only after successful payment. Subscriptions may renew automatically unless cancelled by the user before the next billing cycle.',
        ],
      },
      {
        heading: '6. Pricing',
        body: [
          'The Basic Access plan may be offered at a monthly or annual price as displayed on the website or checkout page. Professional or advanced analytical services may be offered upon request, subject to separate agreement and pricing.',
        ],
      },
      {
        heading: '7. Payments',
        body: [
          'Payments may be processed through third-party payment providers such as Stripe. Heliophant does not store full credit or debit card details on its own servers.',
        ],
      },
      {
        heading: '8. Account Access',
        body: [
          'Users are responsible for maintaining the confidentiality of their login credentials. Sharing access, redistributing paid content, or using the service for unauthorized commercial resale is prohibited.',
        ],
      },
      {
        heading: '9. Intellectual Property',
        body: [
          'All content, models, algorithms, reports, charts, design elements, software, texts, graphics, and analytical materials provided by Heliophant are protected by intellectual property laws. Users receive a limited, non-transferable right to access the service during the active subscription period.',
        ],
      },
      {
        heading: '10. Prohibited Use',
        body: [
          'Users may not copy, resell, redistribute, scrape, reverse engineer, interfere with, or misuse the platform, algorithms, models, or content.',
        ],
      },
      {
        heading: '11. Availability',
        body: [
          'Heliophant aims to provide reliable access but does not guarantee uninterrupted or error-free operation. Temporary downtime may occur due to maintenance, updates, technical issues, or third-party service interruptions.',
        ],
      },
      {
        heading: '12. Limitation of Liability',
        body: [
          'Heliophant shall not be liable for financial losses, trading losses, lost profits, indirect damages, or consequential damages arising from the use or interpretation of the platform, models, reports, or analytical content.',
        ],
      },
      {
        heading: '13. Changes to Services',
        body: ['Heliophant may modify, update, suspend, or discontinue parts of the service at any time.'],
      },
      {
        heading: '14. Changes to Terms',
        body: [
          'Heliophant may update these Terms of Service from time to time. Continued use of the service after changes means acceptance of the updated terms.',
        ],
      },
      {
        heading: '15. Governing Law',
        body: ['These terms are governed by Greek law and applicable European Union law.'],
      },
      {
        heading: '16. Contact',
        body: ['For questions regarding these Terms, contact: contact@heliophant.com'],
      },
    ],
  },

  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'February 2026',
    sections: [
      {
        heading: '1. Introduction',
        body: [
          'This Privacy Policy explains how Heliophant collects, uses, stores, and protects personal data when users access our website, platform, subscription services, or contact forms.',
        ],
      },
      {
        heading: '2. Data We May Collect',
        body: ['We may collect:'],
        bullets: [
          'name and surname',
          'email address',
          'billing details',
          'subscription status',
          'payment confirmation data',
          'contact form messages',
          'technical data such as IP address, browser type, device information, and website usage data',
        ],
      },
      {
        heading: '3. Payment Data',
        body: [
          'Payments may be processed by third-party payment providers such as Stripe. Heliophant does not store full credit or debit card details.',
        ],
      },
      {
        heading: '4. Purpose of Processing',
        body: ['We process personal data for:'],
        bullets: [
          'account creation and service access',
          'subscription management',
          'billing and payment confirmation',
          'customer support',
          'communication with users',
          'service improvement',
          'legal and tax compliance',
          'fraud prevention and platform security',
        ],
      },
      {
        heading: '5. Legal Basis',
        body: ['Personal data may be processed based on:'],
        bullets: [
          'performance of a contract',
          'legal obligations',
          'legitimate business interest',
          'user consent where required',
        ],
      },
      {
        heading: '6. Data Sharing',
        body: [
          'We may share limited necessary data with trusted service providers, including payment processors, hosting providers, email delivery providers, analytics tools, and compliance services.',
        ],
      },
      {
        heading: '7. Data Retention',
        body: [
          'We retain personal data only for as long as necessary for the purposes described above or as required by law, tax, accounting, or regulatory obligations.',
        ],
      },
      {
        heading: '8. User Rights',
        body: [
          'Users may request access, correction, deletion, restriction, or portability of their personal data, subject to applicable law.',
        ],
      },
      {
        heading: '9. Cookies and Analytics',
        body: [
          'The website may use cookies or similar technologies for functionality, analytics, security, and service improvement.',
        ],
      },
      {
        heading: '10. Security',
        body: [
          'We use reasonable technical and organizational measures to protect personal data from unauthorized access, loss, misuse, or alteration.',
        ],
      },
      {
        heading: '11. International Services',
        body: [
          'Some third-party providers may process data outside Greece or the European Union. Where applicable, appropriate safeguards are used.',
        ],
      },
      {
        heading: '12. Contact',
        body: ['For privacy-related requests, contact: contact@heliophant.com'],
      },
    ],
  },

  'refund-policy': {
    title: 'Refund & Cancellation Policy',
    lastUpdated: 'February 2026',
    sections: [
      {
        heading: '1. Subscription Cancellation',
        body: [
          'Users may cancel their subscription at any time. Cancellation prevents future renewal charges but does not automatically refund amounts already paid.',
        ],
      },
      {
        heading: '2. Monthly Subscriptions',
        body: ['Monthly subscriptions remain active until the end of the current billing period after cancellation.'],
      },
      {
        heading: '3. Annual Subscriptions',
        body: ['Annual subscriptions remain active until the end of the paid annual period after cancellation.'],
      },
      {
        heading: '4. Digital Service Access',
        body: [
          'Because Heliophant provides immediate access to digital tools, analytical models, and online content, payments are generally non-refundable once access has been activated, except where required by applicable law.',
        ],
      },
      {
        heading: '5. Duplicate or Erroneous Charges',
        body: [
          'If a user believes they were charged incorrectly, they should contact billing@heliophant.com within 7 days of the charge.',
        ],
      },
      {
        heading: '6. Professional Services',
        body: [
          'Custom analytical or professional services are subject to separate agreement. Refund eligibility, if any, depends on the agreed scope, delivery status, and terms of the specific engagement.',
        ],
      },
      {
        heading: '7. Contact',
        body: ['For billing or cancellation questions, contact: billing@heliophant.com'],
      },
    ],
  },

  disclaimer: {
    title: 'Disclaimer',
    lastUpdated: 'February 2026',
    sections: [
      {
        heading: '1. General Information',
        body: [
          'Heliophant provides scientific data analysis tools, quantitative models, research-oriented market analysis, and educational analytical resources.',
        ],
      },
      {
        heading: '2. No Financial or Investment Advice',
        body: [
          'The information provided by Heliophant does not constitute investment advice, financial advice, trading advice, legal advice, tax advice, or any form of regulated financial service.',
        ],
      },
      {
        heading: '3. No Buy or Sell Recommendations',
        body: [
          'Heliophant does not recommend buying, selling, holding, shorting, leveraging, or trading any financial instrument, cryptocurrency, security, derivative, commodity, index, ETF, or asset.',
        ],
      },
      {
        heading: '4. User Responsibility',
        body: [
          'Users are solely responsible for their own decisions. Any action taken based on Heliophant\u2019s data, models, analysis, or content is taken at the user\u2019s own risk.',
        ],
      },
      {
        heading: '5. Market Risk',
        body: [
          'Financial markets and digital assets involve significant risk. Past performance, backtests, simulations, or historical analysis do not guarantee future results.',
        ],
      },
      {
        heading: '6. Model Limitations',
        body: [
          'All analytical models have limitations. Data may be incomplete, delayed, inaccurate, or affected by market conditions, technical issues, or third-party data providers.',
        ],
      },
      {
        heading: '7. No Guarantee of Results',
        body: [
          'Heliophant does not guarantee profits, performance, accuracy, availability, or any specific financial outcome.',
        ],
      },
      {
        heading: '8. Contact',
        body: ['For questions about this disclaimer, contact: contact@heliophant.com'],
      },
    ],
  },
};

const EL = {
  terms: {
    title: 'Όροι Χρήσης',
    lastUpdated: 'Φεβρουάριος 2026',
    sections: [
      {
        heading: '1. Εισαγωγή',
        body: [
          'Καλώς ήρθατε στο Heliophant.',
          'Η πρόσβαση και χρήση της ιστοσελίδας, των υπηρεσιών, των συνδρομών, των αναλυτικών εργαλείων και του περιεχομένου της πλατφόρμας συνεπάγεται την αποδοχή των παρόντων Όρων Χρήσης.',
        ],
      },
      {
        heading: '2. Στοιχεία Επικοινωνίας',
        body: [
          'Email Επικοινωνίας: contact@heliophant.com',
          'Υποστήριξη: support@heliophant.com',
          'Θέματα Χρέωσης: billing@heliophant.com',
        ],
      },
      {
        heading: '3. Φύση των Υπηρεσιών',
        body: ['Το Heliophant παρέχει:'],
        bullets: [
          'εργαλεία ανάλυσης δεδομένων',
          'ποσοτικά μοντέλα',
          'ερευνητικό περιεχόμενο',
          'αναλυτικές αναφορές',
          'επιστημονικές μελέτες',
          'συνδρομητική πρόσβαση σε ψηφιακές υπηρεσίες',
        ],
      },
      {
        heading: '4. Μη Παροχή Επενδυτικών Συμβουλών',
        body: ['Το Heliophant δεν παρέχει:'],
        bullets: [
          'επενδυτικές συμβουλές',
          'χρηματοοικονομικές συμβουλές',
          'διαχείριση χαρτοφυλακίου',
          'υπηρεσίες διαμεσολάβησης',
          'υπηρεσίες διαχείρισης κεφαλαίων',
          'εξατομικευμένες συστάσεις αγοράς ή πώλησης',
        ],
      },
      {
        heading: '',
        body: [
          'Καμία πληροφορία, ανάλυση, γράφημα, μοντέλο ή αποτέλεσμα που παρέχεται από την πλατφόρμα δεν πρέπει να θεωρείται ως προτροπή αγοράς, πώλησης ή διακράτησης οποιουδήποτε χρηματοοικονομικού μέσου.',
        ],
      },
      {
        heading: '5. Συνδρομές',
        body: [
          'Οι υπηρεσίες παρέχονται μέσω μηνιαίων ή ετήσιων συνδρομών.',
          'Η πρόσβαση ενεργοποιείται μετά την επιτυχή ολοκλήρωση της πληρωμής.',
          'Οι συνδρομές ενδέχεται να ανανεώνονται αυτόματα μέχρι την ακύρωσή τους.',
        ],
      },
      {
        heading: '6. Πληρωμές',
        body: [
          'Οι πληρωμές διεκπεραιώνονται μέσω τρίτων παρόχων πληρωμών όπως η Stripe.',
          'Το Heliophant δεν αποθηκεύει στοιχεία πιστωτικών ή χρεωστικών καρτών.',
        ],
      },
      {
        heading: '7. Πνευματική Ιδιοκτησία',
        body: ['Όλο το περιεχόμενο της πλατφόρμας αποτελεί πνευματική ιδιοκτησία του Heliophant.', 'Απαγορεύεται:'],
        bullets: ['αντιγραφή', 'μεταπώληση', 'αναπαραγωγή', 'αναδιανομή', 'reverse engineering', 'εμπορική εκμετάλλευση'],
      },
      {
        heading: '',
        body: ['χωρίς προηγούμενη έγγραφη άδεια.'],
      },
      {
        heading: '8. Περιορισμός Ευθύνης',
        body: ['Το Heliophant δεν ευθύνεται για:'],
        bullets: [
          'άμεσες ή έμμεσες οικονομικές απώλειες',
          'επενδυτικές ζημίες',
          'απώλεια κερδών',
          'διακοπή υπηρεσιών τρίτων',
          'τεχνικά προβλήματα',
        ],
      },
      {
        heading: '',
        body: ['που προκύπτουν από τη χρήση της πλατφόρμας.'],
      },
      {
        heading: '9. Τροποποίηση Όρων',
        body: ['Οι παρόντες όροι δύνανται να τροποποιούνται οποτεδήποτε.'],
      },
      {
        heading: '10. Εφαρμοστέο Δίκαιο',
        body: ['Οι παρόντες όροι διέπονται από το Ελληνικό Δίκαιο και το Δίκαιο της Ευρωπαϊκής Ένωσης.'],
      },
    ],
  },

  privacy: {
    title: 'Πολιτική Απορρήτου',
    lastUpdated: 'Φεβρουάριος 2026',
    sections: [
      {
        heading: '1. Εισαγωγή',
        body: ['Η προστασία των προσωπικών δεδομένων αποτελεί προτεραιότητα του Heliophant.'],
      },
      {
        heading: '2. Δεδομένα που συλλέγουμε',
        body: ['Ενδέχεται να συλλέγουμε:'],
        bullets: [
          'Ονοματεπώνυμο',
          'Διεύθυνση email',
          'Στοιχεία χρέωσης',
          'Κατάσταση συνδρομής',
          'Μηνύματα επικοινωνίας',
          'Διεύθυνση IP',
          'Στοιχεία συσκευής και browser',
          'Στατιστικά χρήσης',
        ],
      },
      {
        heading: '3. Πληρωμές',
        body: [
          'Οι πληρωμές πραγματοποιούνται μέσω της Stripe.',
          'Το Heliophant δεν αποθηκεύει στοιχεία καρτών.',
        ],
      },
      {
        heading: '4. Σκοποί Επεξεργασίας',
        body: ['Τα δεδομένα χρησιμοποιούνται για:'],
        bullets: [
          'διαχείριση λογαριασμών',
          'ενεργοποίηση συνδρομών',
          'τιμολόγηση',
          'υποστήριξη πελατών',
          'βελτίωση υπηρεσιών',
          'συμμόρφωση με φορολογικές και νομικές υποχρεώσεις',
        ],
      },
      {
        heading: '5. Δικαιώματα Χρηστών',
        body: ['Οι χρήστες έχουν δικαίωμα:'],
        bullets: ['πρόσβασης', 'διόρθωσης', 'διαγραφής', 'περιορισμού επεξεργασίας', 'φορητότητας δεδομένων'],
      },
      {
        heading: '',
        body: ['σύμφωνα με τον GDPR.'],
      },
      {
        heading: '6. Ασφάλεια',
        body: ['Λαμβάνονται κατάλληλα τεχνικά και οργανωτικά μέτρα για την προστασία των δεδομένων.'],
      },
      {
        heading: '7. Επικοινωνία',
        body: ['contact@heliophant.com'],
      },
    ],
  },

  'refund-policy': {
    title: 'Πολιτική Ακυρώσεων & Επιστροφών',
    lastUpdated: 'Φεβρουάριος 2026',
    sections: [
      {
        heading: '1. Ακύρωση Συνδρομής',
        body: [
          'Ο χρήστης μπορεί να ακυρώσει τη συνδρομή του οποιαδήποτε στιγμή.',
          'Η ακύρωση αποτρέπει μελλοντικές χρεώσεις.',
        ],
      },
      {
        heading: '2. Μηνιαίες Συνδρομές',
        body: ['Η πρόσβαση παραμένει ενεργή μέχρι το τέλος της ήδη πληρωμένης περιόδου.'],
      },
      {
        heading: '3. Ετήσιες Συνδρομές',
        body: ['Η πρόσβαση παραμένει ενεργή μέχρι το τέλος του ετήσιου κύκλου που έχει ήδη εξοφληθεί.'],
      },
      {
        heading: '4. Ψηφιακές Υπηρεσίες',
        body: [
          'Λόγω της άμεσης πρόσβασης σε ψηφιακές υπηρεσίες, εργαλεία και αναλυτικό περιεχόμενο, οι πληρωμές γενικά δεν επιστρέφονται μετά την ενεργοποίηση της πρόσβασης, εκτός εάν απαιτείται διαφορετικά από την ισχύουσα νομοθεσία.',
        ],
      },
      {
        heading: '5. Λανθασμένες Χρεώσεις',
        body: [
          'Σε περίπτωση λανθασμένης χρέωσης επικοινωνήστε εντός 7 ημερών:',
          'billing@heliophant.com',
        ],
      },
    ],
  },

  disclaimer: {
    title: 'Αποποίηση Ευθύνης',
    lastUpdated: 'Φεβρουάριος 2026',
    sections: [
      {
        heading: '1. Γενικά',
        body: ['Το Heliophant παρέχει εργαλεία επιστημονικής ανάλυσης δεδομένων και ερευνητικό περιεχόμενο.'],
      },
      {
        heading: '2. Όχι Επενδυτική Συμβουλή',
        body: ['Το περιεχόμενο της πλατφόρμας δεν αποτελεί:'],
        bullets: [
          'επενδυτική συμβουλή',
          'χρηματοοικονομική συμβουλή',
          'φορολογική συμβουλή',
          'νομική συμβουλή',
        ],
      },
      {
        heading: '3. Όχι Συστάσεις Αγοράς ή Πώλησης',
        body: [
          'Το Heliophant δεν προτείνει αγορά, πώληση ή διακράτηση οποιουδήποτε χρηματοοικονομικού προϊόντος.',
        ],
      },
      {
        heading: '4. Ευθύνη Χρήστη',
        body: ['Κάθε απόφαση λαμβάνεται αποκλειστικά με ευθύνη του χρήστη.'],
      },
      {
        heading: '5. Κίνδυνος Αγορών',
        body: [
          'Οι χρηματοοικονομικές αγορές ενέχουν σημαντικό κίνδυνο.',
          'Οι προηγούμενες αποδόσεις δεν εγγυώνται μελλοντικά αποτελέσματα.',
        ],
      },
      {
        heading: '6. Καμία Εγγύηση',
        body: ['Δεν παρέχεται εγγύηση:'],
        bullets: ['κερδοφορίας', 'ακρίβειας', 'διαθεσιμότητας', 'συγκεκριμένου αποτελέσματος'],
      },
    ],
  },
};

export const LEGAL_CONTENT = { en: EN, el: EL };

export const CONTACT_PAGE = {
  en: {
    title: 'Contact',
    lastUpdated: 'February 2026',
    intro: 'Get in touch with the Heliophant team. We typically respond within one business day.',
    phoneLabel: 'Phone',
    phone: '+30 6943893076',
    emails: [
      { label: 'General inquiries', email: 'contact@heliophant.com' },
      { label: 'Billing questions', email: 'billing@heliophant.com' },
      { label: 'Technical support', email: 'support@heliophant.com' },
    ],
    formTitle: 'Send us a message',
    fields: { name: 'Your name', email: 'Your email', subject: 'Subject', message: 'Your message' },
    submit: 'Send message',
    sending: 'Sending...',
    success: 'Message sent. We will reply soon.',
    error: 'Could not send. Please try again or email us directly.',
  },
  el: {
    title: 'Επικοινωνία',
    lastUpdated: 'Φεβρουάριος 2026',
    intro: 'Επικοινωνήστε με την ομάδα του Heliophant. Συνήθως απαντάμε εντός μίας εργάσιμης ημέρας.',
    phoneLabel: 'Τηλέφωνο',
    phone: '+30 6943893076',
    emails: [
      { label: 'Γενικές πληροφορίες', email: 'contact@heliophant.com' },
      { label: 'Θέματα χρέωσης', email: 'billing@heliophant.com' },
      { label: 'Τεχνική υποστήριξη', email: 'support@heliophant.com' },
    ],
    formTitle: 'Στείλτε μας ένα μήνυμα',
    fields: { name: 'Ονοματεπώνυμο', email: 'Email', subject: 'Θέμα', message: 'Μήνυμα' },
    submit: 'Αποστολή μηνύματος',
    sending: 'Αποστολή...',
    success: 'Το μήνυμα στάλθηκε. Θα απαντήσουμε σύντομα.',
    error: 'Δεν ήταν δυνατή η αποστολή. Δοκιμάστε ξανά ή στείλτε μας email απευθείας.',
  },
};
